const AuremeCheckoutUtils = window.AuremeCheckoutUtils;

class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.isAuremeDeliveryAttributesSyncing = false;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    for (const closeButton of this.querySelectorAll('button[type="button"]')) {
      closeButton.addEventListener('click', this.close.bind(this));
    }
    this.bindAuremeCheckoutContextCapture();
    this.setPresentation('default');
  }

  /*
   * checkout submit を一度止め、配送属性と遷移元文脈を同期してから ready flag 付きで再送信する。
   * flag 通過時は通常 submit に戻し、同じ前処理を二重に実行しない。
   */
  bindAuremeCheckoutContextCapture() {
    const checkoutForm = document.getElementById('cart-notification-form');
    if (!checkoutForm || checkoutForm.dataset.auremeCheckoutContextBound === 'true') return;

    checkoutForm.addEventListener('submit', (event) => {
      if (checkoutForm.dataset[AuremeCheckoutUtils.CHECKOUT_READY_FLAG] === 'true') {
        AuremeCheckoutUtils.clearCheckoutReadyFlag(
          checkoutForm,
          AuremeCheckoutUtils.CHECKOUT_READY_FLAG,
        );
        return;
      }

      const submitter = event.submitter;
      event.preventDefault();

      AuremeCheckoutUtils.prepareAndResumeCheckoutSubmit({
        form: checkoutForm,
        onCartItemLimitExceeded: () => {
          window.location.href = routes.cart_url;
        },
        prepareDeliveryAttributes: () => this.prepareAuremeDeliveryAttributesForCheckout(),
        recordContext: () => this.recordAuremeCheckoutContext(),
        resumeSubmit: () => this.resumeAuremeCheckoutSubmit(checkoutForm, submitter),
      });
    });

    checkoutForm.dataset.auremeCheckoutContextBound = 'true';
  }

  updateAuremeAttributes(attributes) {
    const body = JSON.stringify({
      attributes: AuremeCheckoutUtils.buildCartAttributesClearingPointAmount(attributes),
    });

    return fetch(`${routes.cart_update_url}.js`, { ...fetchConfig(), body }).then((response) => {
      if (!response.ok) {
        throw new Error(`cart_update request failed: ${response.status}`);
      }

      return response.json();
    });
  }

  async prepareAuremeDeliveryAttributesForCheckout() {
    if (this.isAuremeDeliveryAttributesSyncing) return;

    /* カート状態を一度だけ取得し、recordAuremeCheckoutContextと共有する */
    const cartState = await AuremeCheckoutUtils.fetchCartState();
    this._lastCartState = cartState;
    await AuremeCheckoutUtils.prepareDeliveryAttributesForCheckout({
      cartState,
      updateAttributes: (attributes) => this.updateAuremeAttributes(attributes),
      isSyncing: this.isAuremeDeliveryAttributesSyncing,
      setSyncing: (isSyncing) => {
        this.isAuremeDeliveryAttributesSyncing = isSyncing;
      },
    });
  }

  async recordAuremeCheckoutContext() {
    try {
      /* prepareAuremeDeliveryAttributesForCheckoutで取得済みのカート状態を再利用 */
      const cartState = this._lastCartState || (await AuremeCheckoutUtils.fetchCartState());
      this._lastCartState = undefined;
      AuremeCheckoutUtils.recordCheckoutContext({ cartState });
    } catch (error) {
      console.error(error);
    }
  }

  resumeAuremeCheckoutSubmit(form, submitter) {
    AuremeCheckoutUtils.resumeCheckoutSubmit({
      form,
      submitter,
      readyFlag: AuremeCheckoutUtils.CHECKOUT_READY_FLAG,
    });
  }

  open() {
    this.notification.classList.add('animate', 'active');
    if (this.presentation === 'aureme-product') {
      document.body.classList.add('overflow-hidden');
    }

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true },
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    document.body.classList.remove('overflow-hidden');

    removeTrapFocus(this.activeElement);
    this.setPresentation('default');
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    for (const section of this.getSectionsToRender()) {
      const sectionElement = document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHtml) continue;

      const sectionInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (sectionInnerHTML === null) continue;

      sectionElement.innerHTML = sectionInnerHTML;
    }

    if (this.header) this.header.reveal();
    this.bindAuremeCheckoutContextCapture();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-form',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;

    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const targetElement = parsedHtml.querySelector(selector);

    return targetElement ? targetElement.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setPresentation(mode = 'default') {
    this.presentation = mode === 'aureme-product' ? 'aureme-product' : 'default';
    this.classList.toggle(
      'cart-notification--aureme-product',
      this.presentation === 'aureme-product',
    );
    this.notification.dataset.presentation = this.presentation;
  }
}

customElements.define('cart-notification', CartNotification);
